// ReportExportManager.tsx
import React, { useEffect } from 'react';

import { getBasePath } from '@ais-ui/api';

import { AsyncJobStatus, useAsyncJobs } from '@ais-ui/async-jobs';
import {
  ExportFormat,
  useCompleteReportExportFetchingProcess,
} from '@ais-ui/websockets';

// Global registry to track export jobs being managed by this component
// This ensures we don't try to start the same job multiple times from different instances
const exportJobRegistry = new Set<string>();

/**
 * How long a download's blob URL stays valid after the click. Revoking it
 * straight away can cancel the download in some browsers; never revoking it
 * pins the CSV blob in memory for the life of the tab. Same delay as
 * FileSaver.js.
 */
const BLOB_URL_REVOKE_DELAY_MS = 40_000;

export const ReportExportManager: React.FC = () => {
  const { jobs, updateJob } = useAsyncJobs();
  const exportProcess = useCompleteReportExportFetchingProcess();

  // Find a pending export job to work on
  useEffect(() => {
    // Skip if we're already busy with an export
    if (exportProcess.isLoading) {
      return;
    }

    // Find a pending export job that isn't being handled yet
    const pendingJob = jobs.find(
      (job) =>
        job.metadata?.type === 'report-export' &&
        (job.status === AsyncJobStatus.PENDING ||
          job.status === AsyncJobStatus.RUNNING) &&
        !exportJobRegistry.has(job.id),
    );

    if (pendingJob) {
      exportJobRegistry.add(pendingJob.id);

      // Start the export process
      exportProcess.startJob({
        jobId: pendingJob.metadata?.reportId,
        format: pendingJob.metadata?.format,
        centerLat: pendingJob.metadata?.centerLat,
        centerLon: pendingJob.metadata?.centerLon,
        zoomLevel: pendingJob.metadata?.zoomLevel,
        minLat: pendingJob.metadata?.minLat,
        minLon: pendingJob.metadata?.minLon,
        maxLat: pendingJob.metadata?.maxLat,
        maxLon: pendingJob.metadata?.maxLon,
      });

      // Update job to show it's running
      updateJob(pendingJob.id, {
        status: AsyncJobStatus.RUNNING,
        startedAt: new Date(),
      });
    }
  }, [jobs, exportProcess, updateJob]);

  // Handle export success
  useEffect(() => {
    if (
      exportProcess.data &&
      exportProcess.currentJobId &&
      exportProcess.currentFormat
    ) {
      // Find the job that was being processed - verify it matches the current export job and format
      const runningJob = jobs.find(
        (job) =>
          job.status === AsyncJobStatus.RUNNING &&
          exportJobRegistry.has(job.id) &&
          job.metadata?.reportId === exportProcess.currentJobId &&
          job.metadata?.format === exportProcess.currentFormat,
      );

      if (runningJob) {
        const format = runningJob.metadata?.format as ExportFormat;
        const reportInfo = runningJob.metadata?.reportInfo;

        // Use hidden iframe for downloads to avoid Firefox navigation issues
        // that close WebSocket connections. Iframe downloads don't trigger
        // page unload events like anchor clicks can in some browsers.
        const triggerDownloadViaIframe = (url: string) => {
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = url;
          document.body.appendChild(iframe);
          // Clean up iframe after download starts
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 5000);
        };

        // For blob URLs, use anchor (same-origin, no issues)
        const triggerDownloadViaAnchor = (
          blobUrl: string,
          filename: string,
        ) => {
          const anchor = document.createElement('a');
          anchor.href = blobUrl;
          anchor.download = filename;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          setTimeout(
            () => URL.revokeObjectURL(blobUrl),
            BLOB_URL_REVOKE_DELAY_MS,
          );
        };

        if (format === 'OF_SHIPLIST' && Array.isArray(exportProcess.data)) {
          const csvContent =
            'name,mmsi,imo\n' +
            exportProcess.data
              .map((item) => `${item.name},${item.mmsi},${item.imo}`)
              .join('\n');
          const blob = new Blob([csvContent], { type: 'text/csv' });
          const downloadUrl = URL.createObjectURL(blob);

          updateJob(runningJob.id, {
            status: AsyncJobStatus.COMPLETED,
            completedAt: new Date(),
            result: downloadUrl,
            progress: 100,
          });

          let filename: string;
          if (reportInfo) {
            const reportOwner = reportInfo['Owner']
              .replace(/\(.*?\)/g, '')
              .trim();
            filename = `${reportInfo['Job name']}-${reportInfo['Report Definition']}-${reportOwner}-job${reportInfo['Job ID']}.csv`;
          } else {
            filename = 'exported_ship_list.csv';
          }

          triggerDownloadViaAnchor(downloadUrl, filename);
          exportJobRegistry.delete(runningJob.id);
        } else {
          // For non-shiplist exports, use iframe to download from server URL.
          // Iframe downloads don't trigger page navigation/unload events,
          // so WebSocket connections stay intact (works in Firefox).
          const baseUrl = getBasePath();
          const fileUrl = baseUrl + exportProcess.data;

          updateJob(runningJob.id, {
            status: AsyncJobStatus.COMPLETED,
            completedAt: new Date(),
            result: fileUrl,
            progress: 100,
          });

          triggerDownloadViaIframe(fileUrl);
          exportJobRegistry.delete(runningJob.id);
        }
      }
    }
  }, [exportProcess.data, jobs, updateJob]);

  useEffect(() => {
    if (exportProcess.isError) {
      const runningJob = jobs.find(
        (job) =>
          job.status === AsyncJobStatus.RUNNING &&
          exportJobRegistry.has(job.id),
      );

      if (runningJob) {
        updateJob(runningJob.id, {
          status: AsyncJobStatus.FAILED,
          completedAt: new Date(),
          error: {
            message: 'Export failed',
          },
        });
        exportJobRegistry.delete(runningJob.id);
      }
    }
  }, [exportProcess.isError, jobs, updateJob]);

  return null;
};
